import React, { useEffect, useRef } from 'react'
import { Box, Typography } from '@mui/material'
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import { CloudUpload } from '@mui/icons-material';
import emptyImg from '../../media/images/empty.png';
import docImage from '../../media/images/document.png';

const FileUploadDefaultImage =  emptyImg;
export type FileUploadProps = {
    imageButton?: boolean
    accept: string
    hoverLabel?: string
    dropLabel?: string
    width?: string
    height?: string
    backgroundColor?: string
    image?: {
        url: string
        imageStyle?: {
            width?: string
            height?: string
            maxHeight?: string
            maxWidth?: string
            objectFit?: string
        }
    }
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void
    onDrop: (event: React.DragEvent<HTMLElement>) => void
}

const useStyle = makeStyles({
    root: {
        cursor: 'pointer',
        textAlign: 'center',
        display: 'flex',
        '&:hover p,&:hover svg,& img': {
            opacity: 1,
        },
        '& p, svg': {
            opacity: 0.4,
        },
        '&:hover img': {
            opacity: 0.3,
        },
    },
    noMouseEvent: {
        pointerEvents: 'none',
    },
    iconText: {
        display: 'flex',
        justifyContent: 'center',
        flexDirection: 'column',
        alignItems: 'center',
        position: 'absolute',
    },
    hidden: {
        display: 'none',
    },
    onDragOver: {
        '& img': {
            opacity: 0.3,
        },
        '& p, svg': {
            opacity: 1,
        },
    },
})

export const FileUpload: React.FC<FileUploadProps> = ({
    accept,
    imageButton = false,
    hoverLabel = 'Click or drag to upload file',
    dropLabel = 'Drop file here',
    width = '200px',
    height = '160px',
    backgroundColor = '#484c64',
    image = {
        url : FileUploadDefaultImage,
        imageStyle : {
            height: 'inherit',
            maxWidth: '100%',
            maxHeight: '100%',
            objectFit: 'contain'
        },
    },
    onChange,
    onDrop,
}) => {
    const fileRef = useRef<HTMLInputElement>(null);
    const classes = useStyle()
    const [imageUrl, setImageUrl] = React.useState('')
    const [labelText, setLabelText] = React.useState<string>(hoverLabel)
    const [isDragOver, setIsDragOver] = React.useState<boolean>(false)
    const [isMouseOver, setIsMouseOver] = React.useState<boolean>(false)
    const stopDefaults = (e: React.DragEvent) => {
        e.stopPropagation()
        e.preventDefault()
    }
    const dragEvents = {
        onMouseEnter: () => {
            setIsMouseOver(true)
        },
        onMouseLeave: () => {
            setIsMouseOver(false)
        },
        onDragEnter: (e: React.DragEvent) => {
            stopDefaults(e)
            setIsDragOver(true)
            setLabelText(dropLabel)
        },
        onDragLeave: (e: React.DragEvent) => {
            stopDefaults(e)
            setIsDragOver(false)
            setLabelText(hoverLabel)
        },
        onDragOver: stopDefaults,
        onDrop: (e: React.DragEvent<HTMLElement>) => {
            stopDefaults(e)
            setLabelText(hoverLabel)
            setIsDragOver(false)
            if (imageButton && e.dataTransfer.files[0]) {
                if(e.dataTransfer.files[0].type.split('/')[0] == 'image') {
                    setImageUrl(URL.createObjectURL(e.dataTransfer.files[0]))
                } else {
                    setImageUrl(docImage)
                }
            } else {
                setImageUrl(emptyImg)
            }
            onDrop(e)
        },
        onChange: (e: any) => {
            stopDefaults(e)
            setLabelText(hoverLabel)
            setIsDragOver(false)
            if (imageButton && e.dataTransfer.files[0]) {
                if(e.dataTransfer.files[0].type.split('/')[0] === 'image') {
                    setImageUrl(URL.createObjectURL(e.dataTransfer.files[0]))
                } else {
                    setImageUrl(docImage)
                }
            } else {
                setImageUrl(emptyImg)
            }
            onChange(e);
        },
    }

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files != null && imageButton && event.target.files[0]) {
            setImageUrl(URL.createObjectURL(event.target.files[0]))
        }

        onChange(event)
    }

    useEffect( () => {
        setImageUrl(image.url);
    }, [image]); 

    return (
        <>
            <input
                onChange={handleChange}
                accept={accept}
                className={classes.hidden}
                id="file-upload"
                type="file"
                ref={fileRef}
            />

            <label
                htmlFor="file-upload"
                {...dragEvents}
                className={clsx(classes.root, isDragOver && classes.onDragOver)}
                style={{justifyContent: 'center'}}
            >
                <Box
                    width={width}
                    height={height}
                    bgcolor={backgroundColor}
                    className={classes.noMouseEvent}
                    borderRadius='10px'
                    display="flex"
                    alignContent={'center'}
                    alignItems="center"
                >
                    {imageButton && (
                        <Box position="absolute" height={height} width={width}>
                            {imageUrl && <img
                            src={imageUrl}
                                alt="file selected"
                                style={{
                                    height: 'inherit',
                                    maxWidth: '100%',
                                    maxHeight: '100%',
                                    objectFit: 'contain'
                                }}
                            />}
                        </Box>
                    )}

                    {(imageButton || isDragOver || isMouseOver) && (
                        <>
                            <Box
                                height={height}
                                width={width}
                                className={classes.iconText}
                            >
                                <CloudUpload fontSize="large" />
                                <Typography>{labelText}</Typography>
                            </Box>
                        </>
                    )}
                </Box>
            </label>
        </>
    )
}
